import {ChangeDetectionStrategy, Component, computed, inject, input, model} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {DirectusTranslationPipe} from '@frontend/directus';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Accordion, AccordionContent, AccordionHeader, AccordionPanel} from 'primeng/accordion';
import {Dialog} from 'primeng/dialog';
import {ProviderScoreCalculationResult} from '../../services/score-calculation/models';
import {
  answer,
  answerCompleteness,
  COMPANY_ANSWER_SCA,
  COMPANY_SCA,
  companyKpis,
  CULTURE_ANSWER_SCA,
  CULTURE_SCA,
  FINANCE_ANSWER_SCA,
  FINANCE_SCA,
  financeKpis,
  INDIVIDUAL_RESULT_SC_NUMBERS,
  percent,
  providerRawAnswers,
  REFERENCE_SCA,
  yesNo,
} from './rfi-individual-results.calc';
import {
  AssessmentGroup,
  AssessmentItem,
  EmployeeBreakdownPart,
  IndividualSubCriteriaScore,
  MetricCard,
  MetricFormat,
  ReferenceAnswer,
} from './rfi-individual-results-modal.types';
import {RfiScoreSummaryCardsComponent} from './parts/rfi-score-summary-cards/rfi-score-summary-cards.component';
import {RfiSectionHeaderComponent} from './parts/rfi-section-header/rfi-section-header.component';
import {RfiFinanceSectionComponent} from './sections/rfi-finance-section/rfi-finance-section.component';
import {RfiCompanySectionComponent} from './sections/rfi-company-section/rfi-company-section.component';
import {RfiCultureSectionComponent} from './sections/rfi-culture-section/rfi-culture-section.component';
import {RfiReferencesSectionComponent} from './sections/rfi-references-section/rfi-references-section.component';

const KEY = 'project.steps.providerResults.individual.';
const MILLION = 1_000_000;
const CHART_COLORS = {revenue: '#668bea', cloudRevenue: '#10b99b', ebitda: '#f5ad41'};
const TERNARY_KEYS = new Map([
  [1, 'provider.tenderResponse.fulfillmentScore.full'],
  [0.5, 'provider.tenderResponse.fulfillmentScore.partly'],
  [0, 'provider.tenderResponse.fulfillmentScore.none'],
]);

@Component({
  selector: 'app-rfi-individual-results-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Dialog,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    TranslocoPipe,
    RfiScoreSummaryCardsComponent,
    RfiSectionHeaderComponent,
    RfiFinanceSectionComponent,
    RfiCompanySectionComponent,
    RfiCultureSectionComponent,
    RfiReferencesSectionComponent,
  ],
  templateUrl: './rfi-individual-results-modal.component.html',
})
export class RfiIndividualResultsModalComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly directusTranslation = new DirectusTranslationPipe();

  readonly visible = model(false);
  readonly toolName = input<string | undefined>();
  readonly hrToolDirectusId = input<string | undefined>();
  readonly calculationResult = input<ProviderScoreCalculationResult | null>(null);
  readonly subCriteriaScores = input<IndividualSubCriteriaScore[]>([]);

  protected readonly financeScNumber = INDIVIDUAL_RESULT_SC_NUMBERS.finance;
  protected readonly companyScNumber = INDIVIDUAL_RESULT_SC_NUMBERS.company;
  protected readonly cultureScNumber = INDIVIDUAL_RESULT_SC_NUMBERS.culture;
  protected readonly referenceScNumber = INDIVIDUAL_RESULT_SC_NUMBERS.references;

  private readonly language = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  private readonly answers = computed(() => {
    const result = this.calculationResult();
    const hrToolDirectusId = this.hrToolDirectusId();
    return result && hrToolDirectusId
      ? providerRawAnswers(result.requestedCriteria, hrToolDirectusId)
      : new Map<number, number | null>();
  });
  private readonly finance = computed(() => financeKpis(this.answers()));
  private readonly company = computed(() => companyKpis(this.answers()));

  protected readonly financeCompleteness = computed(() =>
    answerCompleteness(this.answers(), FINANCE_ANSWER_SCA),
  );
  protected readonly companyCompleteness = computed(() =>
    answerCompleteness(this.answers(), COMPANY_ANSWER_SCA),
  );
  protected readonly cultureCompleteness = computed(() =>
    answerCompleteness(this.answers(), CULTURE_ANSWER_SCA),
  );
  protected readonly referenceCompleteness = computed(() =>
    answerCompleteness(this.answers(), [...REFERENCE_SCA]),
  );

  protected readonly financeCards = computed((): MetricCard[] => {
    const finance = this.finance();
    return [
      this.card(
        'equityRatio',
        this.metric('percent', finance.equityRatio, 0),
        'equityRatioSource',
        [FINANCE_SCA.equityRatio],
      ),
      this.card(
        'revenueGrowth',
        this.metric('signedPercent', finance.revenueGrowth),
        'revenueGrowthSource',
        [...FINANCE_SCA.revenue],
      ),
      this.card(
        'ebitdaMargin',
        this.metric('percent', finance.ebitdaMargin),
        'ebitdaMarginSource',
        [FINANCE_SCA.ebitda[2], FINANCE_SCA.revenue[2]],
      ),
      this.card(
        'cloudRevenueShare',
        this.metric('percent', finance.cloudRevenueShare),
        'cloudRevenueShareSource',
        [FINANCE_SCA.cloudRevenue[2], FINANCE_SCA.revenue[2]],
      ),
      this.card(
        'recurringRevenueShare',
        this.metric('percent', finance.recurringRevenueShare, 0),
        'recurringRevenueShareSource',
        [FINANCE_SCA.recurringRevenueShare],
      ),
      this.card(
        'largestCustomerShare',
        this.metric('percent', finance.largestCustomerShare, 0),
        'largestCustomerShareSource',
        [FINANCE_SCA.largestCustomerShare],
      ),
      this.card(
        'researchRatio',
        this.metric('percent', finance.researchRatio, 0),
        'researchRatioSource',
        [FINANCE_SCA.researchRatio],
      ),
      this.card('marketYears', this.metric('number', finance.marketYears), 'marketYearsSource', [
        FINANCE_SCA.marketYears,
      ]),
      this.card(
        'liabilityInsurance',
        this.metric('yesNo', finance.liabilityInsurance),
        'liabilityInsuranceSource',
        [FINANCE_SCA.liabilityInsurance],
      ),
    ];
  });

  protected readonly financeChartData = computed(() => {
    const years = this.finance().years;
    if (
      years.every(
        (year) => year.revenue === null && year.cloudRevenue === null && year.ebitda === null,
      )
    ) {
      return null;
    }
    const language = this.language();
    const dataset = (labelKey: string, color: string, values: (number | null)[]) => ({
      label: this.transloco.translate(`${KEY}${labelKey}`, {}, language),
      backgroundColor: color,
      data: values.map((value) => (value === null ? null : value / MILLION)),
    });
    return {
      labels: years.map((year) =>
        this.transloco.translate(`${KEY}yearsAgo.${year.yearsAgo}`, {}, language),
      ),
      datasets: [
        dataset(
          'totalRevenue',
          CHART_COLORS.revenue,
          years.map((year) => year.revenue),
        ),
        dataset(
          'cloudRevenue',
          CHART_COLORS.cloudRevenue,
          years.map((year) => year.cloudRevenue),
        ),
        dataset(
          'ebitda',
          CHART_COLORS.ebitda,
          years.map((year) => year.ebitda),
        ),
      ],
    };
  });

  protected readonly companyCards = computed((): MetricCard[] => {
    const company = this.company();
    return [
      this.card(
        'activeCustomers',
        this.metric('number', company.activeCustomers),
        'activeCustomersSource',
        [COMPANY_SCA.activeCustomers],
      ),
      this.card(
        'grossRevenueRetention',
        this.metric('percent', company.grossRevenueRetention, 0),
        'grossRevenueRetentionSource',
        [COMPANY_SCA.grossRevenueRetention],
      ),
      this.card(
        'hrCloudEmployees',
        this.metric('number', company.hrCloudEmployees),
        'hrCloudEmployeesSource',
        [COMPANY_SCA.hrCloudEmployees],
      ),
      this.card(
        'employeeGrowth',
        this.metric('signedPercent', company.employeeGrowth),
        'employeeGrowthSource',
        [...COMPANY_SCA.employees],
      ),
      this.card(
        'implementationPartners',
        this.metric('number', company.implementationPartners),
        'implementationPartnersSource',
        [COMPANY_SCA.implementationPartners],
      ),
      this.card(
        'technologyPartners',
        this.metric('number', company.technologyPartners),
        'technologyPartnersSource',
        [COMPANY_SCA.technologyPartners],
      ),
      this.card('iso27001', this.metric('yesNo', company.iso27001), 'iso27001Source', [
        COMPANY_SCA.iso27001,
      ]),
      this.card(
        'dataCentersInEea',
        this.metric('yesNo', company.dataCentersInEea),
        'dataCentersInEeaSource',
        [COMPANY_SCA.dataCentersInEea],
      ),
    ];
  });
  protected readonly employeeBreakdown = computed((): EmployeeBreakdownPart[] => {
    const company = this.company();
    const parts = [
      {labelKey: `${KEY}development`, value: company.development},
      {labelKey: `${KEY}operations`, value: company.operations},
      {labelKey: `${KEY}customerSupport`, value: company.customerSupport},
    ];
    const largest = Math.max(1, ...parts.map((part) => part.value ?? 0));
    return parts.map((part) => ({...part, share: ((part.value ?? 0) / largest) * 100}));
  });

  protected readonly cultureGroups = computed((): AssessmentGroup[] => [
    {
      titleKey: `${KEY}cultureStrategy`,
      items: [
        this.yesNoItem('companyValues', CULTURE_SCA.values),
        this.yesNoItem('companyStrategy', CULTURE_SCA.strategy),
      ],
    },
    {
      titleKey: `${KEY}customerOrientation`,
      items: [this.customerSatisfactionItem()],
    },
    {
      titleKey: `${KEY}governance`,
      items: [
        this.yesNoItem('compliancePolicies', CULTURE_SCA.compliancePolicies),
        this.yesNoItem('codeOfConduct', CULTURE_SCA.codeOfConduct),
        this.yesNoItem('whistleblowing', CULTURE_SCA.whistleblowing),
        this.yesNoItem('pendingLitigation', CULTURE_SCA.pendingLitigation, true),
      ],
    },
    {
      titleKey: `${KEY}sustainability`,
      items: [
        this.yesNoItem('socialProjects', CULTURE_SCA.socialProjects),
        this.yesNoItem('esgReporting', CULTURE_SCA.esgReporting),
      ],
    },
  ]);

  protected readonly referenceAnswers = computed((): ReferenceAnswer[] => {
    const language = this.language();
    const attributes = Object.values(this.calculationResult()?.attributesMap ?? {}).flat();
    return REFERENCE_SCA.map((scaNumber) => {
      const attribute = attributes.find((item) => item.sca_number === scaNumber);
      const value = answer(this.answers(), scaNumber);
      return {
        scaNumber,
        label:
          this.directusTranslation.transform(
            attribute?.translations,
            language,
            `SCA ${scaNumber}`,
          ) ?? `SCA ${scaNumber}`,
        fulfillmentKey: value === null ? null : (TERNARY_KEYS.get(value) ?? null),
      };
    });
  });

  protected readonly evaluatedScores = computed(() =>
    this.subCriteriaScores().filter((score) => score.evaluated),
  );

  protected score(scNumber: number): IndividualSubCriteriaScore | undefined {
    return this.evaluatedScores().find((score) => score.scNumber === scNumber);
  }

  private card(
    labelKey: string,
    value: MetricCard['value'],
    sourceKey: string,
    scaNumbers: number[],
  ): MetricCard {
    return {labelKey: `${KEY}${labelKey}`, value, sourceKey: `${KEY}${sourceKey}`, scaNumbers};
  }

  private yesNoItem(
    labelKey: string,
    scaNumber: number,
    unfavorableWhenYes = false,
  ): AssessmentItem {
    const value = yesNo(this.answers(), scaNumber);
    return {
      labelKey: `${KEY}${labelKey}`,
      value: this.metric('yesNo', value),
      favorable: value === null ? null : value !== unfavorableWhenYes,
    };
  }

  private customerSatisfactionItem(): AssessmentItem {
    const value = percent(this.answers(), CULTURE_SCA.customerSatisfaction);
    return {
      labelKey: `${KEY}customerSatisfaction`,
      value: this.metric('percent', value, 0),
      favorable: value === null ? null : value > 0,
    };
  }

  private metric(
    format: MetricFormat,
    amount: number | boolean | null,
    maxFractionDigits = 1,
  ): MetricCard['value'] {
    return {format, amount, maxFractionDigits: format === 'number' ? 0 : maxFractionDigits};
  }
}
